import { richTextTableSx } from './richTextTableSx';

/**
 * HTML shown in campaign/list quiz cards: optional short description replaces full description.
 * Full `description` is still used when taking a quiz (not preview).
 */
export function getQuizPreviewHtml(quiz) {
  if (!quiz) return '';
  const short = (quiz.short_description || '').trim();
  if (short) return quiz.short_description;
  return quiz.description || '';
}

/** Stops Chrome/Google Translate from rewriting authored quiz copy (and stale SPA text). */
export const quizNoTranslateProps = {
  className: 'notranslate',
  translate: 'no',
};

/** Shared styles for rendered quiz HTML (images stay in flow, full width of container). */
export const quizRichContentSx = {
  mb: 2,
  color: 'text.secondary',
  fontSize: '0.875rem',
  '& p': { margin: '0.65em 0' },
  '& ul, & ol': { margin: '0.65em 0', paddingLeft: '20px' },
  '& p:empty': { minHeight: '1em' },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  ...richTextTableSx,
};

/**
 * TipTap / ProseMirror HTML must render inside a div, not Typography (which outputs <p> and breaks nested <p> spacing).
 * Used for quiz bodies, dealer and manufacturer campaign detail views.
 * Blank lines often serialize as empty <p></p>; p:empty min-height avoids collapsed vertical gaps/margin collapsing.
 */
export const quizRichHtmlBlockSx = {
  fontSize: '1rem',
  lineHeight: 1.7,
  '& p': {
    margin: '0.75em 0',
  },
  '& p:empty': {
    minHeight: '1.15em',
  },
  '& h1, & h2, & h3, & h4': {
    marginTop: '1em',
    marginBottom: '0.5em',
    fontWeight: 600,
  },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.35rem' },
  '& h3': { fontSize: '1.2rem' },
  '& ul, & ol': {
    margin: '0.75em 0',
    paddingLeft: '1.5em',
  },
  '& li': {
    margin: '0.35em 0',
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    my: 1,
  },
  '& strong': { fontWeight: 600 },
  ...richTextTableSx,
};

/** Max completed attempts per quiz (must match api/quiz_utils.QUIZ_MAX_ATTEMPTS). */
export const QUIZ_MAX_ATTEMPTS = 3;

/** Label for quiz reward on dealer cards (flat or per-question). */
export function getQuizRewardLabel(quiz, t, namespace = 'quiz') {
  if (!quiz?.reward || quiz.reward <= 0) return null;
  if (quiz.reward_per_question) {
    return t(`${namespace}.rewardPerQuestion`, { points: quiz.reward });
  }
  return t(`${namespace}.rewardPointsValue`, { points: quiz.reward });
}

/** Points credited to the dealer for their latest quiz attempt. */
export function getQuizPointsAwarded(quiz) {
  const attempt = quiz?.user_attempt;
  if (!attempt?.reward_awarded) return 0;
  const awarded = Number(attempt.points_awarded);
  if (Number.isFinite(awarded) && awarded > 0) return awarded;
  if (quiz.reward_per_question && attempt.score != null) {
    return attempt.score * quiz.reward;
  }
  return quiz.reward || 0;
}

/** Whether dealers should see correct answers on the results screen. */
export function shouldRevealCorrectAnswers(quiz, attemptCount) {
  const setting = quiz?.show_correct_answers || 'ALWAYS';
  if (setting === 'NEVER') return (attemptCount || 0) >= QUIZ_MAX_ATTEMPTS;
  if (setting === 'AFTER_SECOND_ATTEMPT') return (attemptCount || 0) >= 2;
  return true;
}
